import { Alert, AlertTitle, Box, Button, Divider, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { createInvoice } from 'src/shared/services/invoiceService';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useState } from 'react';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { InvoiceRequest } from 'src/modules/invoice/models/invoiceRequest';
import dayjs from 'dayjs';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { InvoiceMissionTypeEnum } from 'src/shared/models/enums/invoiceMissionType.enum';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { CreateInvoiceSelectMissionType } from './createInvoiceSelectInvoiceMissionType';
import { CreateInvoiceMemberPicker } from './createInvoiceMemberPicker';
import { CreateInvoiceArticleRows } from './createInvoiceArticleRows';
import { GetInvoiceMissionTypes } from './invoiceMissionTypes';

type CreateInvoiceModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

enum InvoiceModeEnum {
  OtherMember = 'othermember',
  Member = 'member',
  NewMember = 'newmember',
  NewCompany = 'newcompany',
}

export const CreateInvoiceModal = (props: CreateInvoiceModalProps) => {
  const { modalOpen, onClose, onSave } = props;
  const { t } = useTranslation(['common', 'invoice']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [customerId, setCustomerId] = useState<number>();
  const [invoiceMissionId, setInvoiceMissionId] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<number>();
  const [createInvoiceMode, setCreateInvoiceMode] = useState<InvoiceModeEnum>(InvoiceModeEnum.OtherMember);
  const handleInvoiceModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateInvoiceMode((event.target as HTMLInputElement).value as InvoiceModeEnum);
  };

  const createInvoiceMissionTypes = GetInvoiceMissionTypes({ otherContactMode: false, showMemberFee: false });

  const hasOwnerId = useCallback(() => ownerId !== undefined && ownerId !== 0, [ownerId]);

  const amountValidationMessage = t('common:validationMessages.amount');
  const requiredMessage = t('common:validationMessages.required');

  const invoiceRowSchema = yup.object().shape({
    articleNr: yup.string().min(1, requiredMessage).required(requiredMessage),
    amount: yup.number().required(amountValidationMessage).min(1, amountValidationMessage).typeError(amountValidationMessage),
    text: yup.string().required(requiredMessage),
  });

  const CreateInvoiceSchema: Schema<InvoiceRequest> = yup.object().shape({
    customer_id: yup.number().required(),
    invoiceMission_id: yup.mixed<InvoiceMissionTypeEnum>().required(requiredMessage),
    owner_id: yup.number().required(),
    dueDate: yup.string().required(requiredMessage),
    invoiceText: yup.string().optional(),
    billText: yup.string().optional(),
    heading: yup.string().optional(),
    subHeading: yup.string().optional(),
    rows: yup.array().of(invoiceRowSchema).required(),
    firstName: yup.string().optional(),
    lastName: yup.string().optional(),
    address: yup.string().optional(),
    addressOther: yup.string().optional(),
    coAddress: yup.string().optional(),
    postNr: yup.string().optional(),
    postAddress: yup.string().optional(),
    country: yup.string().optional(),
  });

  const formMethods = useForm<InvoiceRequest>({
    resolver: yupResolver(CreateInvoiceSchema),
    mode: 'all',
    defaultValues: {
      customer_id: 0,
      invoiceMission_id: InvoiceMissionTypeEnum.None,
      owner_id: 0,
      dueDate: DateHelper.getFSDate(dayjs(new Date()).add(1, 'month')),
      invoiceText: '',
      billText: '',
      heading: '',
      subHeading: '',
      rows: [{ articleNr: '0', amount: 0, text: '' }],
      firstName: '',
      lastName: '',
      address: '',
      addressOther: '',
      coAddress: '',
      postNr: '',
      postAddress: '',
      country: '',
    },
  });

  const { handleSubmit, setValue } = formMethods;

  const onHandleClose = () => {
    onClose();
  };

  const onHandleSave = () => {
    onSave();
  };

  const updateInvoiceMissionId = (id) => {
    setInvoiceMissionId(id);
    setValue('invoiceMission_id', id);
  };

  const onSubmit = useCallback(
    async (data: InvoiceRequest) => {
      // eslint-disable-next-line no-param-reassign
      if (hasOwnerId() && ownerId !== undefined) data.owner_id = ownerId;
      if ((createInvoiceMode === InvoiceModeEnum.Member || createInvoiceMode === InvoiceModeEnum.OtherMember) && customerId) {
        // eslint-disable-next-line no-param-reassign
        data.customer_id = customerId;
      }

      setLoading(true);

      const dataToPost: InvoiceRequest = {
        ...data,
        dueDate: DateHelper.getFSDate(data.dueDate),
      };

      try {
        const result = await createInvoice(dataToPost);
        if (isMounted()) {
          toast.success(<>{t('invoice:createInvoice.success')}</>);
          navigate(`${RoutesEnum.InvoicesDetail}/${result.data}`);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [hasOwnerId, ownerId, createInvoiceMode, customerId, isMounted, t, navigate],
  );

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    if (modalOpen) {
      setInvoiceMissionId(null);
    }
  }, [modalOpen]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogTitle={t('invoice:createInvoice.modalHeader')}
      showDialogActions={invoiceMissionId !== null}
      padding={0}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 3 }}>
                {invoiceMissionId === null && (
                  <CreateInvoiceSelectMissionType createInvoiceMissionTypes={createInvoiceMissionTypes} callback={(x) => updateInvoiceMissionId(x)} />
                )}
                {invoiceMissionId !== null && (
                  <Box sx={{ mb: 3 }}>
                    <InvoiceGroups
                      callback={(ownId) => {
                        setOwnerId(ownId);
                      }}
                      placeholder='Välj'
                      emptyLabel='Välj'
                      autoSelect
                      ownerId={ownerId}
                    />
                    {!hasOwnerId() && (
                      <Alert severity='info' sx={{ mt: 2 }}>
                        <AlertTitle>{t('invoice:createInvoice.selectOwnerIdWarningTitle')}</AlertTitle>
                        {t('invoice:createInvoice.selectOwnerIdWarningText')}
                      </Alert>
                    )}
                  </Box>
                )}
                {hasOwnerId() && invoiceMissionId !== null && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormFieldset legend={t('invoice:createInvoice.recipientHeader')}>
                        <Box sx={{ p: 1.5, pt: 2.5 }}>
                          <GridWrapper narrow>
                            <Grid item xs={12} sx={{ pt: `0 !important`, mb: -0.5 }}>
                              <RadioGroup row value={createInvoiceMode} onChange={handleInvoiceModeChange}>
                                <FormControlLabel
                                  value={InvoiceModeEnum.OtherMember}
                                  control={<Radio />}
                                  label={t('invoice:createInvoice.mode.otherMember')}
                                />
                                <FormControlLabel value={InvoiceModeEnum.Member} control={<Radio />} label={t('invoice:createInvoice.mode.member')} />
                                <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 1, width: '100%' }} />
                                <FormControlLabel
                                  label={t('invoice:createInvoice.mode.createNewHeader')}
                                  disabled
                                  control={<Radio sx={{ display: 'none' }} />}
                                  sx={{
                                    ml: { xs: 0, md: 2 },
                                    width: {
                                      xs: '100%',
                                      md: 'auto',
                                    },
                                    display: {
                                      xs: 'block',
                                      md: 'inherit',
                                    },
                                    '& .MuiFormControlLabel-label': {
                                      fontWeight: 500,
                                      fontSize: '1.1rem',
                                      pl: { xs: 0, md: 2 },
                                      color: (thm) => `${thm.palette.text.primary} !important`,
                                    },
                                  }}
                                />
                                <FormControlLabel
                                  value={InvoiceModeEnum.NewMember}
                                  control={<Radio />}
                                  label={t('invoice:createInvoice.mode.newMember')}
                                />
                                <FormControlLabel
                                  value={InvoiceModeEnum.NewCompany}
                                  control={<Radio />}
                                  label={t('invoice:createInvoice.mode.newCompany')}
                                />
                              </RadioGroup>
                            </Grid>
                            {(createInvoiceMode === InvoiceModeEnum.Member || createInvoiceMode === InvoiceModeEnum.OtherMember) && (
                              <Grid item xs={12}>
                                <CreateInvoiceMemberPicker
                                  onlyOtherMembers={createInvoiceMode === InvoiceModeEnum.OtherMember}
                                  label={
                                    createInvoiceMode === InvoiceModeEnum.OtherMember
                                      ? t('invoice:createInvoice.searchOtherMember')
                                      : t('invoice:createInvoice.searchMember')
                                  }
                                  selectedMemberCallback={(x) => setCustomerId(x)}
                                />
                              </Grid>
                            )}

                            {createInvoiceMode === InvoiceModeEnum.NewMember && (
                              <>
                                <Grid item xs={6}>
                                  <ControlledTextField name='firstName' label={t('invoice:createInvoice.firstName')} />
                                </Grid>
                                <Grid item xs={6}>
                                  <ControlledTextField name='lastName' label={t('invoice:createInvoice.lastName')} />
                                </Grid>
                              </>
                            )}
                            {createInvoiceMode === InvoiceModeEnum.NewCompany && (
                              <Grid item xs={12}>
                                <ControlledTextField name='lastName' label={t('invoice:createInvoice.companyName')} />
                              </Grid>
                            )}
                            {(createInvoiceMode === InvoiceModeEnum.NewMember || createInvoiceMode === InvoiceModeEnum.NewCompany) && (
                              <>
                                <Grid item xs={6}>
                                  <ControlledTextField name='address' label={t('invoice:createInvoice.address')} />
                                </Grid>
                                <Grid item xs={3}>
                                  <ControlledTextField name='postNr' label={t('invoice:createInvoice.postNr')} />
                                </Grid>
                                <Grid item xs={3}>
                                  <ControlledTextField name='postAddress' label={t('invoice:createInvoice.postAddress')} />
                                </Grid>
                              </>
                            )}
                          </GridWrapper>
                        </Box>
                      </FormFieldset>
                    </Grid>
                    <Grid item xs={4}>
                      <ControlledDatePicker name='dueDate' label={t('invoice:createInvoice.dueDate')} />
                    </Grid>
                    <Grid item xs={8}>
                      <ControlledSelectBox
                        name='invoiceMission_id'
                        label={t('invoice:createInvoice.invoiceMissionType')}
                        items={createInvoiceMissionTypes.map((item) => ({
                          label: item.label,
                          value: item.key,
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CreateInvoiceArticleRows />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField name='heading' label={t('invoice:createInvoice.invoiceRow.heading')} />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField multiline name='subHeading' label={t('invoice:createInvoice.invoiceMessageHeader')} rows={8} />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
      dialogActions={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='outlined' sx={{ mr: 1 }} onClick={onHandleClose}>
            {t('common:button.cancel')}
          </Button>
          <LoadingButton loading={loading} type='submit' variant='contained' disableElevation onClick={handleSubmitClick}>
            {t('common:button.save')}
          </LoadingButton>
        </Box>
      }
    />
  );
};
